import React, { useCallback, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { AddIcon, Button, Checkbox, Flex, Input, ProviderConsumer, SearchIcon, SyncIcon } from '@fluentui/react-northstar';
import { platformService } from '../shared/services/platform.service';
import { Communication } from '@fluentui/react-teams';
import { Providers } from '@microsoft/mgt-element/dist/es6/providers/Providers';
import { Breadcrumbs } from '../shared/components/BreadcrumbNavigation';
import { CONTACT_ADMIN, DEFAULT_API_STATUS, DONE, ERROR, GETTING_ALL_USERS, LOADING, SOMETHING_WENT_WRONG, SYNC_USERS, UNAUTHORIZED_ACCESS, USERS, ADMINISTRATORS, ANALYSTS, AGENTS, UPDATE, SETTINGS, PRODUCT_LICENSE, ADD_USER, FILTERS_STRINGS, LICENSE_FEATURE, ConfigurationName } from '../shared/utils/constants';
import { toTitleCase } from '../shared/utils/helper';
import { CheckLogin } from '../shared/components/CheckLogin';
import { Helmet } from 'react-helmet';
import { LoadingScreen } from '../shared/components/LoadingScreen';
import { useTranslation } from 'react-i18next';
import { appState } from '../AppState';
import { EnumSortOrderType } from '../tikit/toolbar/CustomViewContextProvider';
import { IColumn } from '@fluentui/react/lib/DetailsList';
import UserDetailList from './UserDetailList';
import { toolbarButtonColor } from '../tikit/ticketHelper';
import { Toolbar } from '../shared/components/Toolbar';
import { getCachedFeature } from '../shared/cache/FeatureCache';
import { EmptyData } from '../shared/components/EmptyData';
import EmptySVG from './../../svg/empty.svg';
import { ResultCount } from '../shared/components/ResultCount';
import { clearUserCache } from '../shared/cache/PlatformUserCache';

export const UserTable = () => {

  const appContextState = appState();
  const { t } = useTranslation();
  const api = new platformService();

  const [data, setData] = useState([]);
  const [sortOrder, setSortOrder] = useState({ orderBy: '', orderType: EnumSortOrderType.asc });
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLicensedUser, setIsLicensedUser] = useState(false);
  const [isExternalUsersEnabled, setIsExternalUsersEnabled] = useState(false);
  const [isAddUserEnabled, setIsAddUserEnabled] = useState(true);
  const archiveStorageKey = `isArchivedUsersList`;
  const [showArchived, setShowArchived] = useState(localStorage.getItem(archiveStorageKey) === "true");
    
  const [apiStatus, setApiStatus] = useState<APIStatus>(DEFAULT_API_STATUS as APIStatus);
  const location = useLocation();
  const history = useHistory();


  const sortData = (column: IColumn) => {
    setSortOrder({
      orderBy: column.key,
      orderType: column.key === sortOrder.orderBy && sortOrder.orderType === EnumSortOrderType.asc ? EnumSortOrderType.desc : EnumSortOrderType.asc
    });
  }
  const orderData = (a: any, b: any, by: string, dir: EnumSortOrderType): number => {
    let result = 0;
    if(by === 'IsExternalUser') {
      result = (a[by] === b[by]) ? 0 : a[by] ? 1 : -1;
    }else
      result = (a[by] ?? "").localeCompare(b[by] ?? "")
    return (dir === EnumSortOrderType.desc) ? result * -1 : result;
  }
  const filterData = (data: any[]) => {
    let filteredData = data;

    const term = searchTerm.toLocaleLowerCase();
    filteredData = filteredData.filter(data => (data.FullName.toLocaleLowerCase().includes(term) ?? false) || (data.Email?.toLocaleLowerCase().includes(term) ?? false) ||
      (data.OfficePhone?.toLocaleLowerCase().includes(term) ?? false) || (data.MobilePhone?.toLocaleLowerCase().includes(term) ?? false) || (data.Department?.toLocaleLowerCase().includes(term) ?? false) ||
      (data.JobTitle?.toLocaleLowerCase().includes(term) ?? false) || (data.productNames?.toLocaleLowerCase().includes(term) ?? false) ||
      (isExternalUsersEnabled && ((data.Source?.toLocaleLowerCase().includes(term) ?? false) ||
        (data.IsExternalUser ? t('common.buttons.yes') : t('common.buttons.no')).toLocaleLowerCase().includes(term)))
    );

    if (sortOrder.orderBy !== "")
      filteredData = filteredData.sort((a, b) => orderData(a, b, sortOrder.orderBy, sortOrder.orderType));

    return filteredData;
  };


  const fetchData = useCallback(async (displayArchived: boolean = showArchived) => {
    setApiStatus({ ...apiStatus, msg: GETTING_ALL_USERS });
    try {
      const ifArchived = displayArchived ? '?' : `${FILTERS_STRINGS.ARCHIVE_FALSE}&`;
      const isExternalUsersEnabled = await getCachedFeature(LICENSE_FEATURE.ExternalUsers);
      const filterQuery = isExternalUsersEnabled ? ifArchived : `${ifArchived.length > 1 ? `${ifArchived.substring(0, ifArchived.length - 1)} and ` : ifArchived}Source eq 'Entra'&`;
      const result = await Promise.all([
        api.getUsers(`${filterQuery}$orderby=FullName`),
        api.getUserLicenses("?$expand=PlatformUser&$orderby=PlatformUser/UserName"),
        api.getTenantLicenses(),
        api.getSystemSetting(ConfigurationName.Ticket)
      ]);

      setIsLicensedUser(await getCachedFeature(PRODUCT_LICENSE.AnalystAccess));
      setIsExternalUsersEnabled(isExternalUsersEnabled);
      setIsAddUserEnabled(result[3].data["EnableUserCreation"] ?? false);

      const platformUsers = result[0].data.value;
      const licenses = result[1].data.value;
      const products = result[2].data.value;

      const rowData = platformUsers.map((user: any) => {
        const userLicenses = licenses.filter(x => x.PlatformUserId === user.Id);
        let productNames = [];
        products.forEach(product => {
          if (userLicenses.some(x => x.ProductLicenseId === product.ProductLicenseId)) {
            productNames.push(product.ProductName);
          }
        });

        return { ...user, productNames: productNames.join() };
      });

      setData(rowData);
      setApiStatus({ ...apiStatus, status: DONE });
    } catch (err: any) {
      console.error(err);
      setApiStatus({ ...apiStatus, status: ERROR, errCode: err?.response?.status ?? -1 });
    }
  }, []);

  // "Super Agent" is this app's display name for the Analysts role (see the superagents ->
  // analysts alias in SecurityPage.tsx) - Add User is narrower than page access (which also
  // admits Agents), restricted to Administrators or Analysts only.
  const canAddUser = appContextState.userRoles.roles.includes(ADMINISTRATORS) || appContextState.userRoles.roles.includes(ANALYSTS);

  const loadPage = () => {
    const hasAccess = appContextState.userRoles.roles.includes(ADMINISTRATORS) || appContextState.userRoles.roles.includes(ANALYSTS) || appContextState.userRoles.roles.includes(AGENTS);
    if (!hasAccess)
      setApiStatus({ ...apiStatus, status: ERROR, errCode: 401 })
    else
      fetchData();
  }

  const getNavigation = (): Breadcrumbs => {
    const items: Breadcrumbs = {
      breadcrumbs: []
    };

    if (location.pathname.indexOf('/settings/') >= 0)
      items.breadcrumbs.push({ title: t('users.breadcrumb-settings'), link: '/settings' })

    items.breadcrumbs.push({ title: t('users.breadcrumb-user'), link: '' });
    return items;
  };

  const syncAllUsers = async () => {
    setApiStatus({ ...apiStatus, status: LOADING, msg: SYNC_USERS });

    try {
      await clearUserCache();
      await api.syncAllUsers();
      await fetchData();
    } catch (err: any) {
      setApiStatus({
        ...apiStatus,
        status: ERROR,
        errCode: err.response.status
      });
    } finally {
      setApiStatus({ ...apiStatus, status: DONE });
    }
  };

  const errConfig = {
    fields: {
      title:
        apiStatus.errCode === 401
          ? UNAUTHORIZED_ACCESS
          : SOMETHING_WENT_WRONG,
      desc: CONTACT_ADMIN
    }
  };

  const onArchiveSwitchChange = () => {
    const isArchived = localStorage.getItem(archiveStorageKey) === "true";
    localStorage.setItem(archiveStorageKey, !isArchived ? "true" : "false");
    setShowArchived(!isArchived);
    fetchData(!isArchived);
  }

  return (
    <CheckLogin onSignedIn={loadPage}>
      <Helmet>
        <title>{toTitleCase(USERS)} - Tikit</title>
      </Helmet>
      {apiStatus.status === LOADING && (<LoadingScreen message={apiStatus.msg} />)}
      {apiStatus.status === ERROR && (<>
        <Communication {...errConfig} />
        {apiStatus.errCode === 401 && (
          <Button
            content={t('common.refresh-session')}
            primary
            onClick={e => {
              Providers.globalProvider.login();
            }}
          />
        )}
      </>)}
      {apiStatus.status === DONE && (<ProviderConsumer render={(globalTheme) => {
        const filteredItems = filterData(data);

        return <>
          <Toolbar globalTheme={globalTheme} title={`${toTitleCase(SETTINGS)} > ${toTitleCase(USERS)}`} breadcrumbs={getNavigation()} bottomMargin='-1rem' addNew
            addNewContent={<>
            {isExternalUsersEnabled && isAddUserEnabled && canAddUser && <Button
              icon={<AddIcon />}
              text
              primary
              content={ADD_USER}
              disabled={!isLicensedUser}
              onClick={() => {
                if (isLicensedUser) {
                  const path = "/users/0";
                  history.push(path);
                }
              }}
              style={{
                color: toolbarButtonColor(isLicensedUser, globalTheme.siteVariables.colorScheme),
                height: '52px'
              }}
            />}
            <Button icon={<SyncIcon />} text primary disabled={!isLicensedUser} content={UPDATE} onClick={syncAllUsers} style={{ color: toolbarButtonColor(isLicensedUser, globalTheme.siteVariables.colorScheme) }} /> </>} />

          <Flex className='m-2' style={{ justifyContent: 'flex-end' }} gap="gap.medium">
            <Checkbox label={t('preferences.show-archived')} toggle checked={showArchived} onChange={onArchiveSwitchChange} />
            <Input icon={<SearchIcon />} inverted placeholder={t('common.search')} value={searchTerm} onChange={(e, p) => setSearchTerm(p.value)} />
          </Flex>

          <div className='flex' style={{ height: "calc(100vh - 150px - 2.25rem)" }}>
            <div className="w-full overflow-auto" data-is-scrollable="true">
              {filteredItems.length <= 0 && <EmptyData headerText={t('common.no-users-in-the-system')} subheaderText={''} SVGIcon={<EmptySVG width={200} height={200} />} />}
              {filteredItems.length > 0 && <UserDetailList rows={filteredItems} sortOrder={sortOrder} onColumnClick={sortData} isUserLicensed={isLicensedUser} reloadPage={loadPage} isExternalUsersEnabled={isExternalUsersEnabled} />}
            </div>
          </div>
          <div className="h-9 flex items-center"><ResultCount count={filteredItems.length} /></div>
        </>;
      }} />)}
    </CheckLogin>
  );
};
